import { useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Upload, X, Check, Wallet, QrCode, Mail } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";

export default function PayPalSettings({ user, onUpdate }) {
  const { t } = useLocale();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    paypal_email: user?.paypal_email || "",
    paypal_email_confirm: "",
    paypal_username: user?.paypal_username || "",
    paypal_qr_code: user?.paypal_qr_code || ""
  });
  const [emailVerified, setEmailVerified] = useState(!!user?.paypal_email);
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (value) => {
    setFormData(prev => ({ ...prev, paypal_email: value }));
    setEmailVerified(false);
    
    // Clear errors when user types
    if (errors.paypal_email) {
      setErrors(prev => ({ ...prev, paypal_email: null }));
    }
  };

  const handleEmailConfirmChange = (value) => {
    setFormData(prev => ({ ...prev, paypal_email_confirm: value }));
    
    // Clear errors when user types
    if (errors.paypal_email_confirm) {
      setErrors(prev => ({ ...prev, paypal_email_confirm: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate email format
    if (formData.paypal_email && !validateEmail(formData.paypal_email)) {
      newErrors.paypal_email = t("pp.errInvalidEmail");
    }

    // Check emails match
    if (formData.paypal_email && formData.paypal_email !== formData.paypal_email_confirm) {
      newErrors.paypal_email_confirm = t("pp.errNoMatch");
    }

    // Require confirmation for new/changed emails
    if (formData.paypal_email !== (user?.paypal_email || "") && !formData.paypal_email_confirm) {
      newErrors.paypal_email_confirm = t("pp.errConfirm");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error(t("pp.errFix"));
      return;
    }

    setLoading(true);

    try {
      await api.put("/users/profile", {
        paypal_email: formData.paypal_email || null,
        paypal_username: formData.paypal_username || null,
        paypal_qr_code: formData.paypal_qr_code || null
      });

      toast.success(t("pp.savedOk"));
      setEmailVerified(true);
      setFormData(prev => ({ ...prev, paypal_email_confirm: "" }));
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("pp.saveFail"));
    } finally {
      setLoading(false);
    }
  };

  const handleQRUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error(t("pp.errImg"));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("pp.errSize"));
      return;
    }

    setUploading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await api.post("/upload/paypal-qr", formDataUpload, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      setFormData(prev => ({ ...prev, paypal_qr_code: response.data.url }));
      toast.success(t("pp.qrUploaded"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("pp.qrFail"));
    } finally {
      setUploading(false);
    }
  };

  const removeQRCode = () => {
    setFormData(prev => ({ ...prev, paypal_qr_code: "" }));
  };

  const hasChanges = 
    formData.paypal_email !== (user?.paypal_email || "") ||
    formData.paypal_username !== (user?.paypal_username || "") ||
    formData.paypal_qr_code !== (user?.paypal_qr_code || "");

  const canSave = hasChanges && (!formData.paypal_email || validateEmail(formData.paypal_email));

  return (
    <div className="space-y-4">
      {/* Current Balance Display */}
      <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/20">
        <p className="text-sm text-muted-foreground mb-1">{t("pp.balance")}</p>
        <p className="text-3xl font-bold text-green-500" data-testid="paypal-balance">
          ${(user?.balance || 0).toFixed(2)}
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          {t("pp.balanceNote")}
        </p>
      </div>

      <div className="space-y-4">
        {/* PayPal Email */}
        <div>
          <Label htmlFor="paypal_email" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            {t("pp.email")}
          </Label>
          <Input
            id="paypal_email"
            data-testid="paypal-email-input"
            type="email"
            placeholder="your.email@example.com"
            className={`mt-1 ${errors.paypal_email ? 'border-red-500' : ''}`}
            value={formData.paypal_email}
            onChange={(e) => handleEmailChange(e.target.value)}
          />
          {errors.paypal_email && (
            <p className="text-xs text-red-500 mt-1">{errors.paypal_email}</p>
          )}
          {emailVerified && formData.paypal_email === user?.paypal_email && (
            <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
              <Check className="h-3 w-3" /> {t("pp.verified")}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            {t("pp.emailHint")}
          </p>
        </div>

        {/* PayPal Email Confirmation */}
        {formData.paypal_email && formData.paypal_email !== (user?.paypal_email || "") && (
          <div>
            <Label htmlFor="paypal_email_confirm" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              {t("pp.confirmEmail")}
            </Label>
            <Input
              id="paypal_email_confirm"
              data-testid="paypal-email-confirm-input"
              type="email"
              placeholder={t("pp.confirmPh")}
              className={`mt-1 ${errors.paypal_email_confirm ? 'border-red-500' : ''}`}
              value={formData.paypal_email_confirm}
              onChange={(e) => handleEmailConfirmChange(e.target.value)}
            />
            {errors.paypal_email_confirm && (
              <p className="text-xs text-red-500 mt-1">{errors.paypal_email_confirm}</p>
            )}
            {formData.paypal_email_confirm && formData.paypal_email === formData.paypal_email_confirm && (
              <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                <Check className="h-3 w-3" /> {t("pp.emailsMatch")}
              </p>
            )}
          </div>
        )}

        {/* PayPal Username */}
        <div>
          <Label htmlFor="paypal_username" className="flex items-center gap-2">
            <Wallet className="h-4 w-4" />
            {t("pp.username")}
          </Label>
          <Input
            id="paypal_username"
            data-testid="paypal-username-input"
            type="text"
            placeholder="@yourusername"
            className="mt-1"
            value={formData.paypal_username}
            onChange={(e) => setFormData(prev => ({ ...prev, paypal_username: e.target.value }))}
          />
        </div>

        {/* PayPal QR Code */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <QrCode className="h-4 w-4" />
            {t("pp.qr")}
          </Label>

          {formData.paypal_qr_code ? (
            <div className="relative inline-block">
              <img
                src={formData.paypal_qr_code}
                alt="PayPal QR Code"
                className="h-40 w-40 rounded-lg border border-border object-contain bg-white"
              />
              <button
                type="button"
                onClick={removeQRCode}
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div>
              <label
                htmlFor="qr-upload"
                className="flex flex-col items-center justify-center h-40 w-40 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors"
              >
                {uploading ? (
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                    <p className="text-xs text-muted-foreground text-center px-2">
                      {t("pp.qrUpload")}
                    </p>
                  </>
                )}
              </label>
              <input
                id="qr-upload"
                data-testid="paypal-qr-upload-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleQRUpload}
                disabled={uploading}
              />
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            {t("pp.qrHint")}
          </p>
        </div>
      </div>

      {/* Save Button */}
      {hasChanges && (
        <Button
          onClick={handleSave}
          disabled={loading || !canSave}
          className="w-full"
          data-testid="paypal-save-btn"
        >
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          <Check className="mr-2 h-4 w-4" />
          {t("pp.save")}
        </Button>
      )}

      {/* Info */}
      <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
        <p className="text-sm text-blue-400 font-medium mb-2">{t("pp.howTitle")}</p>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• {t("pp.how1")}</li>
          <li>• {t("pp.how2")}</li>
          <li>• {t("pp.how3")}</li>
          <li>• {t("pp.how4")}</li>
        </ul>
      </div>
    </div>
  );
}
